import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  Check,
  ChevronsUpDown,
  Coins,
  Contact,
  FileText,
  Gift,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Package,
  Receipt,
  Settings as SettingsIcon,
  ShoppingCart,
  Tags,
  UserRound,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTenant } from "@/context/TenantContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBarActions";
import { SUPPORT_CHANGED, onChange } from "@/lib/events";
import { noteVisit } from "@/lib/lastPage";
import { cn } from "@/lib/utils";


/**
 * Two counts in one place: the filled one is what's waiting on you, the
 * outlined one is what's already in hand. A single count that included both
 * stayed lit after you'd replied, which taught you to ignore it.
 */
function NavBadge({ action = 0, pending = 0 }) {
  if (!action && !pending) return null;
  return (
    <span className="ml-auto flex items-center gap-1 shrink-0">
      {action > 0 && (
        <span
          title={`${action} waiting on a reply`}
          className="bg-brand text-white text-[10.5px] font-mono rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
        >
          {action > 9 ? "9+" : action}
        </span>
      )}
      {pending > 0 && (
        <span
          title={`${pending} in progress`}
          className="border border-line text-muted2 text-[10.5px] font-mono rounded-full px-1.5 py-[1px] min-w-[18px] text-center"
        >
          {pending > 9 ? "9+" : pending}
        </span>
      )}
    </span>
  );
}

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart },
  { to: "/sales", label: "Sales", icon: Receipt },
  { to: "/customers", label: "Customers", icon: Contact },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/expenses", label: "Expenses", icon: Receipt },
  { to: "/gift-boxes", label: "Gift boxes", icon: Gift, module: "gift_boxes" },
  { to: "/reorder", label: "Reorder", icon: AlertTriangle, module: "reorder" },
  { to: "/valuation", label: "Valuation", icon: Coins, module: "valuation" },
  { to: "/categories", label: "Categories", icon: Tags },
  { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
  { to: "/team", label: "Team", icon: Users, adminOnly: true },
  { to: "/support", label: "Support", icon: LifeBuoy, badge: "support" },
  // Everyone gets this, including staff — Settings is admin-only, and it's
  // where someone would otherwise look for their own password.
  { to: "/profile", label: "Your profile", icon: UserRound },
];

/**
 * For the owner this is a switcher — which client am I in, and jump to another.
 * For client staff it's just their business name, since they only have one.
 */
function ClientBadge({ onNavigate }) {
  const { isOwner, tenants, tenantId, setTenantId, activeTenant } = useTenant();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const name = activeTenant?.name || settings.business_name || "Inventory";
  const initial = (name || "?")[0].toUpperCase();
  const isWorkspace = !!activeTenant?.is_workspace;

  const badge = (
    <>
      <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center shrink-0 shadow-sm">
        <span className="font-display text-white text-[15px] font-semibold leading-none">
          {initial}
        </span>
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="font-display text-[16px] font-semibold tracking-[-0.01em] text-ink leading-none truncate">
          {name}
        </div>
        <div className="eyebrow mt-1 leading-none">
          {isWorkspace ? "Your sandbox" : isOwner ? "Client" : "Inventory"}
        </div>
      </div>
    </>
  );

  if (!isOwner) {
    return <div className="flex items-center gap-2.5 w-full">{badge}</div>;
  }

  const switchTo = (id) => {
    setTenantId(id);
    setOpen(false);
    onNavigate?.();
    navigate("/dashboard");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2.5 w-full rounded-xl p-1 -m-1 hover:bg-brand-light/50 transition-colors">
          {badge}
          <ChevronsUpDown className="w-4 h-4 text-muted2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[232px] p-0" align="start">
        <div className="px-3 py-2 border-b border-line">
          <div className="eyebrow">Switch client</div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {tenants.map((t) => (
            <button
              key={t.tenant_id}
              onClick={() => switchTo(t.tenant_id)}
              className={cn(
                "w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-light/60 transition-colors",
                t.tenant_id === tenantId && "bg-brand-light"
              )}
            >
              <Check
                className={cn(
                  "w-4 h-4 text-brand shrink-0",
                  t.tenant_id === tenantId ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="text-[13px] text-ink truncate flex-1">{t.name}</span>
              <span className="font-mono text-[11px] text-muted2 shrink-0">{t.item_count}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-line p-1">
          <Link
            to="/overview"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-muted2 hover:bg-brand-light/60 hover:text-ink transition-colors"
          >
            <Building2 className="w-4 h-4" />
            Platform overview
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children, title, subtitle, action }) {
  const { user, logout } = useAuth();
  const { isOwner, setTenantId } = useTenant();
  const { isOn, labelFor, loaded } = useSettings();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [support, setSupport] = useState({ unread: 0, open: 0 });

  // Remember where they've been, so a support report can suggest the page it's
  // probably about.
  useEffect(() => {
    noteVisit(pathname);
  }, [pathname]);

  // A reply is easy to miss, so unread answers show as a count on the tab.
  useEffect(() => {
    let cancelled = false;
    const check = () =>
      api
        .get("/support/mine/summary")
        .then(
          ({ data }) =>
            !cancelled &&
            setSupport({ unread: data.unread || 0, open: data.open || 0 })
        )
        .catch(() => {});
    check();
    const timer = setInterval(check, 20000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    const off = onChange(SUPPORT_CHANGED, check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      off();
    };
  }, [pathname]);
  const canAdmin = ["owner", "admin"].includes(user?.role);

  const doLogout = async () => {
    setTenantId(null);
    await logout();
    navigate("/login");
  };

  // A switched-off feature leaves the menu entirely, and an optional one can
  // carry the client's own word for it.
  //
  // Until settings are known, optional items are held back rather than assumed
  // on — showing a tab and then removing it looks like a glitch, and briefly
  // advertises a feature the client has switched off.
  const items = NAV.filter((n) => {
    if (n.adminOnly && !canAdmin) return false;
    if (!n.module) return true;
    return loaded && isOn(n.module);
  }).map((n) => (n.module ? { ...n, label: labelFor(n.module) } : n));

  const sidebar = (
    <>
      <div className="h-[68px] flex items-center px-4 border-b border-line">
        <ClientBadge onNavigate={() => setOpen(false)} />
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {items.map((n) => {
          const active = pathname === n.to || pathname.startsWith(`${n.to}/`);
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors",
                active
                  ? "bg-brand-light text-brand"
                  : "text-muted2 hover:bg-brand-light/50 hover:text-ink"
              )}
            >
              <n.icon className="w-[17px] h-[17px] shrink-0" />
              {n.label}
              {n.badge === "support" && (
                // Filled: replies they haven't read. Outlined: still open, but
                // the ball is with support.
                <NavBadge
                  action={support.unread}
                  pending={Math.max(support.open - support.unread, 0)}
                />
              )}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-line">
        {isOwner && (
          <Link
            to="/clients"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3.5 py-2.5 mb-1 rounded-xl text-[13.5px] font-medium text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors"
          >
            <Building2 className="w-[17px] h-[17px] shrink-0" />
            All clients
          </Link>
        )}
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[13px] shrink-0 hover:ring-2 hover:ring-brand-ring transition-all"
            title="Your profile"
          >
            {(user?.name?.[0] || "U").toUpperCase()}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-ink truncate">{user?.name}</div>
            <div className="text-[11.5px] text-muted2">
              {user?.role === "owner" ? "Platform owner" : user?.role}
            </div>
          </div>
          <button
            onClick={doLogout}
            title="Sign out"
            className="text-muted2 hover:text-stock-out transition-colors p-1.5 rounded-lg hover:bg-rose-50"
          >
            <LogOut className="w-[17px] h-[17px]" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[248px] bg-white border-r border-line flex-col fixed h-screen z-30">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-ink/30 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-[264px] bg-white border-r border-line flex flex-col z-50 animate-rise">
            {sidebar}
          </aside>
        </>
      )}

      <div className="lg:ml-[248px] min-w-0">
        <TopBar
          title={title}
          subtitle={subtitle}
          action={action}
          onMenu={() => setOpen((v) => !v)}
          menuOpen={open}
          scope="tenant"
          searchPlaceholder="Search items, customers, suppliers"
        />
        {/*
          Capped so a wide monitor doesn't stretch a table row until the item
          name and its status sit a foot apart. Wider than most content needs,
          so nothing feels cramped on a laptop.
        */}
        <main className="px-4 sm:px-7 py-6 pb-16 min-w-0">
          <div className="mx-auto w-full max-w-[1500px]">{children}</div>
        </main>
      </div>

    </div>
  );
}
